import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Cookies from "js-cookie";
import { teleConsultationModel } from "../Axios/AxiosConfig";
import loadingimg from "../Assets/images/loading image 1.png";
import { CircularProgress,IconButton  } from "@mui/material";
import { format, startOfWeek, endOfWeek, isWithinInterval } from "date-fns";
import { ChevronLeft } from '@mui/icons-material';

const AllAppointmentsPage = () => {
    const [appointments, setAppointments] = useState([]);
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visibleCount, setVisibleCount] = useState(); 
    const [searchTerm, setSearchTerm] = useState("");
    const [filterDate, setFilterDate] = useState("all"); 

    const navigate = useNavigate();
    
    const observer = useRef();

    const doctorDetailsCookie = Cookies.get("Doctor_details");
    let Doctor_details = {};
    if (doctorDetailsCookie) {
        try {
            Doctor_details = JSON.parse(doctorDetailsCookie);
        } catch (error) {
            console.error("Error parsing Doctor_details cookie:", error);
        }
    }

    const getAppointmentData = async () => {
        if (!Doctor_details?.UID) {
            console.error("Doctor UID is missing!", Doctor_details);
            return;
        }

        try {
            const res = await teleConsultationModel.post("/getDoctorsAppointment", {
                Doc_id: Doctor_details.UID,
            });

            if (res.status === 200) {
                setAppointments(res.data || []);
                setFilteredAppointments(res.data || []); // Initialize search data
            } else {
                console.error("Failed to fetch appointments:", res.statusText);
            }
        } catch (err) {
            console.error("Error fetching appointments:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (Doctor_details?.UID) {
            getAppointmentData();
        } else {
            console.error("Doctor UID is missing or undefined");
        }
    }, [Doctor_details?.UID]);

    // Handle search and filter
    useEffect(() => {
        let filtered = appointments.filter((appointment) =>
            appointment.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            appointment.appointment_date?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            appointment.appointment_time?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            appointment.appointment_status?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Apply date filter
        if (filterDate !== "all") {
            filtered = filtered.filter((appointment) => {
                const appointmentDate = new Date(appointment.appointment_date);
                const now = new Date();
                let start, end;

                switch (filterDate) {
                    case "month":
                        start = new Date(now.getFullYear(), now.getMonth(), 1);
                        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                        break;
                    case "year":
                        start = new Date(now.getFullYear(), 0, 1);
                        end = new Date(now.getFullYear(), 11, 31);
                        break;
                    case "week":
                        start = startOfWeek(now);
                        end = endOfWeek(now);
                        break;
                    case "day":
                        start = new Date(now.setHours(0, 0, 0, 0));
                        end = new Date(now.setHours(23, 59, 59, 999));
                        break;
                    default:
                        return true;
                }

                return isWithinInterval(appointmentDate, { start, end });
            });
        }

        setFilteredAppointments(filtered);
        setVisibleCount(10); // Reset visible count on search/filter change
    }, [searchTerm, filterDate, appointments]);

    // Infinite Scroll: Load more items when reaching the end
    const lastElementRef = useCallback(
        (node) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) {
                    setVisibleCount((prev) => prev + 10); // Load 10 more items
                }
            });
            if (node) observer.current.observe(node);
        },
        [loading]
    );

    return (
        <>
        <nav className='flex items-center p-2 mt-2 ml-2 mr-2 border rounded-lg cursor-pointer'>
            <Link to="/Appointments" className="flex items-center w-full">
                <IconButton>
                <ChevronLeft />
                </IconButton>
                <p className="ml-2 text-lg font-medium">Appointment</p>
            </Link>
        </nav>

        <div className="m-5 bg-white p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
                <h2 className="text-xl font-semibold text-gray-700">Doctor's Appointments</h2>
                <div className="flex space-x-4">
                    <input
                        type="text"
                        placeholder="Search by Patient Name..."
                        className="border px-3 py-2 rounded-lg outline-none"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select
                        className="border px-3 py-2 rounded-lg outline-none"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                    >
                        <option value="all">All Time</option>
                        <option value="day">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="year">This Year</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <p className="text-center text-gray-500">Loading appointments...</p>
            ) : filteredAppointments.length === 0 ? (
                <p className="text-center font-semibold text-gray-700">No appointments found</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border border-gray-300 rounded-lg">
                        <thead className="bg-gray-200 text-gray-700">
                            <tr>
                                <th className="px-4 py-2 border border-gray-300 text-center">Date</th>
                                <th className="px-4 py-2 border border-gray-300">Time</th>
                                <th className="px-4 py-2 border border-gray-300">Patient Name</th>
                                <th className="px-4 py-2 border border-gray-300">Status</th>
                                <th className="px-4 py-2 border border-gray-300"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAppointments.slice(0, visibleCount).map((appointment, index) => (
                                <tr
                                    key={index}
                                    className="border border-gray-300 hover:bg-gray-100"
                                    ref={index === visibleCount - 1 ? lastElementRef : null}
                                >
                                    <td className="px-4 py-2 border border-gray-300 text-center">
                                        {appointment.appointment_date
                                            ? format(new Date(appointment.appointment_date), "dd-MMM-yyyy")
                                            : "N/A"}
                                    </td>
                                    <td className="px-4 py-2 border border-gray-300 text-center">
                                        {appointment.appointment_time || "N/A"}
                                    </td>
                                    <td className="px-4 py-2 border border-gray-300 text-center">
                                        {appointment.patient_name || "N/A"}
                                    </td>
                                    <td className="px-4 py-2 border border-gray-300 text-center">
                                        {appointment.appointment_status || "N/A"}
                                    </td>
                                    <td className="px-4 py-2 border border-gray-300 text-center">
                                        <button
                                            onClick={() => navigate(`/patient/${appointment.Appointment_id}`)}
                                            className="px-3 py-2 border border-orange-500 text-orange-500 font-medium rounded hover:bg-orange-500 hover:text-white"
                                        >
                                            View Profile
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {loading && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "#fdfdfd",
                        zIndex: 9999,
                    }}
                >
                    <div style={{ position: "relative", width: "10rem", height: "10rem" }}>
                        <CircularProgress style={{ width: "100%", height: "100%", color: "#DA6E0B" }} thickness={1} />
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <img src={loadingimg} style={{ width: "80%", height: "80%" }} alt="Loading" />
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

export default AllAppointmentsPage;
